import type { Parent, WindowsConfig } from '@disdis/shared'
import { DEFAULT_WINDOWS, WindowsConfigSchema } from '@disdis/shared'
import { deleteMeta, getMeta, setMeta } from '../state/db.js'
import { setAuthHeader } from '../state/sync.js'

/**
 * The parent's device side session. CONTRACTS §8: it lives under the `parentSession` meta key
 * and nothing else about the parent is written to the device. The PIN hash is here on purpose,
 * API.md §2: the gate verifies it offline.
 */

export type ConsentType = 'kvkk' | 'camera' | 'research'

export type ParentSession = {
  parentId: string
  email: string
  token: string
  expiresAt: string
  locale: 'tr' | 'en'
  emailVerified: boolean
  /** argon2id, from PUT /api/pin. Null until a PIN is set on this device. */
  pinHash: string | null
  /** Latest granted version per document, so a new version re prompts (COMPLIANCE §4). */
  consents: Partial<Record<ConsentType, string>>
  researchConsent: boolean
  /** PAR-01: five wrong tries lock the gate, and a reload must not clear the count. */
  gate: { failures: number; lockedUntil: number }
}

export const GATE_MAX_FAILURES = 5
export const GATE_LOCK_MS = 5 * 60 * 1000
export const GATE_GRACE_MS = 5 * 60 * 1000

export async function getParentSession(): Promise<ParentSession | null> {
  return (await getMeta<ParentSession>('parentSession')) ?? null
}

export async function setParentSession(session: ParentSession): Promise<void> {
  await setMeta('parentSession', session)
  setAuthHeader(`Bearer ${session.token}`)
}

export async function patchParentSession(
  patch: Partial<ParentSession>,
): Promise<ParentSession | null> {
  const current = await getParentSession()
  if (!current) return null
  const next = { ...current, ...patch }
  await setParentSession(next)
  return next
}

export async function clearParentSession(): Promise<void> {
  await deleteMeta('parentSession')
  await deleteMeta('parentGateUntil')
  setAuthHeader(null)
}

/** Restores the bearer header after a reload so the API sees the session on Capacitor too. */
export async function restoreAuthHeader(): Promise<ParentSession | null> {
  const session = await getParentSession()
  if (session) setAuthHeader(`Bearer ${session.token}`)
  return session
}

export function sessionFrom(
  parent: Parent,
  token: string,
  expiresAt: string,
  previous: ParentSession | null,
): ParentSession {
  const consents: Partial<Record<ConsentType, string>> = {}
  for (const grant of parent.consents) consents[grant.type] = grant.version
  return {
    parentId: parent.id,
    email: parent.email,
    token,
    expiresAt,
    locale: parent.locale,
    emailVerified: parent.emailVerified,
    pinHash: previous?.parentId === parent.id ? (previous.pinHash ?? null) : null,
    consents,
    researchConsent: parent.researchConsent,
    gate: { failures: 0, lockedUntil: 0 },
  }
}

/* ------------------------------------------------------------------- windows */

/** The brush windows the ritual reads (ARCHITECTURE §4.8 `windows`). */
export async function getWindows(): Promise<WindowsConfig> {
  const stored = await getMeta<unknown>('windows')
  const parsed = WindowsConfigSchema.safeParse(stored)
  return parsed.success ? parsed.data : DEFAULT_WINDOWS
}

export async function setWindows(windows: WindowsConfig): Promise<void> {
  await setMeta('windows', windows)
}

/* ---------------------------------------------------------------- capability */

export async function openGate(now = Date.now()): Promise<void> {
  await setMeta('parentGateUntil', now + GATE_GRACE_MS)
  await patchParentSession({ gate: { failures: 0, lockedUntil: 0 } })
}

/** CONTRACTS §7: the capability is renewed on activity while the parent is on a screen. */
export async function renewGate(now = Date.now()): Promise<void> {
  const until = await getMeta<number>('parentGateUntil')
  if ((until ?? 0) <= now) return
  await setMeta('parentGateUntil', now + GATE_GRACE_MS)
}

export async function closeGate(): Promise<void> {
  await deleteMeta('parentGateUntil')
}
